import { Alert, Button, Card, CardBody, Input } from '@foundation/ui'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './forgot-password.module.css'
import { api } from '@/lib/api'
import { isAuthenticated } from '@/lib/auth-store'

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: () => {
    if (isAuthenticated()) {
      throw redirect({ to: '/dashboard' })
    }
  },
  component: ForgotPasswordPage,
})

function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [apiError, setApiError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const validateForm = (): boolean => {
    if (!email) {
      setError('Email is required')
      return false
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email')
      return false
    }
    setError(null)
    return true
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setApiError(null)

    if (!validateForm()) return

    setIsLoading(true)

    api
      .requestPasswordReset({ email })
      .then(() => {
        setEmailSent(true)
      })
      .catch((err: unknown) => {
        setApiError(err instanceof Error ? err.message : 'Failed to send reset email')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (emailSent) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <CardBody>
            <div className={styles.logo}>
              <span className={styles.logoIcon}>🔄</span>
              <span className={styles.logoText}>BounceBack</span>
            </div>
            <h2 className={styles.title}>Check your email</h2>
            <p className={styles.subtitle}>
              If an account exists for <strong>{email}</strong>, we&apos;ve sent password reset
              instructions.
            </p>
            <Alert variant="info" className={styles.alert}>
              If you don&apos;t see the email, check your spam folder.
            </Alert>
            <div className={styles.actions}>
              <Link to="/login" className={styles.fullWidth}>
                <Button variant="primary" className={styles.fullWidth}>
                  Back to login
                </Button>
              </Link>
            </div>
          </CardBody>
        </Card>
      </main>
    )
  }

  return (
    <main className={styles.container}>
      <Card className={styles.card}>
        <CardBody>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>🔄</span>
            <span className={styles.logoText}>BounceBack</span>
          </div>
          <h2 className={styles.title}>Reset your password</h2>
          <p className={styles.subtitle}>
            Enter your email and we&apos;ll send you instructions to reset your password.
          </p>

          {apiError && (
            <Alert variant="danger" className={styles.alert}>
              {apiError}
            </Alert>
          )}

          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              {...(error && { error })}
              required
            />

            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                loading={isLoading}
                className={styles.fullWidth}
              >
                Send reset instructions
              </Button>
            </div>
          </form>

          <p className={styles.footer}>
            Remember your password? <Link to="/login">Sign in</Link>
          </p>
        </CardBody>
      </Card>
    </main>
  )
}
